// Link builder for sharing and web-to-app handoff.
//
// Two modes:
// - buildCampaignLink(): Direct UTM links for web use
// - dynamicLinkForResource(): LinkForty short links for web-to-app handoff (QR codes, share)
//   The Rails endpoint mints/caches the LinkForty short link and appends the
//   visitor's utm_* attribution so it forwards to the redirect destination.

import { getFirstTouchData, getLastTouchData } from '../attribution/attributionCapture';

export const LINKFORTY_DOMAIN = 'https://links.hiiker.com';

export interface CampaignParams {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
}

export type ResourceType = 'Hike' | 'Trail' | 'Recording' | 'Country' | 'Region';

export interface ResourceDescriptor {
  type: ResourceType;
  id: string | number;
  campaign?: string;
  fallbackUrl?: string;
}

/**
 * Build a direct campaign link with UTM params (for web use).
 */
export function buildCampaignLink(url: string, params: CampaignParams): string {
  const u = new URL(url);
  if (params.source) u.searchParams.set('utm_source', params.source);
  if (params.medium) u.searchParams.set('utm_medium', params.medium);
  if (params.campaign) u.searchParams.set('utm_campaign', params.campaign);
  if (params.content) u.searchParams.set('utm_content', params.content);
  if (params.term) u.searchParams.set('utm_term', params.term);
  return u.toString();
}

/**
 * Pulls the visitor's last-touch (falling back to first-touch) UTM context
 * from the attribution-capture cookies, so it can be forwarded to LinkForty.
 */
function visitorUtm(): Record<string, string | undefined> {
  const data = getLastTouchData() || getFirstTouchData() || {};
  return {
    utm_source: data.utm_source,
    utm_medium: data.utm_medium,
    utm_campaign: data.utm_campaign,
    utm_content: data.utm_content,
    utm_term: data.utm_term,
  };
}

/**
 * Resolve a LinkForty short link for the given resource via the Rails API.
 * The backend caches a short link per (resource, campaign), so repeated calls
 * are cheap. Visitor utm_* context is forwarded and appended by the backend.
 * Falls back to the resource's canonical URL on failure.
 */
export async function dynamicLinkForResource(descriptor: ResourceDescriptor): Promise<string> {
  const params = new URLSearchParams({
    type: descriptor.type,
    id: String(descriptor.id),
  });
  if (descriptor.campaign) params.set('campaign', descriptor.campaign);
  for (const [k, v] of Object.entries(visitorUtm())) {
    if (v) params.set(k, v);
  }

  try {
    const res = await fetch(`/api/v1/linkforty/short-link?${params.toString()}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const body = (await res.json()) as { url?: string };
    return body.url ?? descriptor.fallbackUrl ?? '';
  } catch {
    return descriptor.fallbackUrl ?? '';
  }
}

/**
 * Resolve a LinkForty QR code link for the given resource.
 */
export function qrCodeLinkForResource(descriptor: Omit<ResourceDescriptor, 'campaign'>): Promise<string> {
  return dynamicLinkForResource({ ...descriptor, campaign: 'web-qr-code' });
}
